/**
 * useSearchFilters — EXL-672
 *
 * Manages faceted search filter state via URL query parameters.
 * URL is the single source of truth; filters persist across page navigation
 * and are shareable via URL.
 *
 * Debouncing (300ms) applies to UI-triggered changes.
 * Browser back/forward (popstate) bypasses debounce and applies immediately.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SearchFiltersState {
  categories: string[];
  type: string;           // '' means 'all'
  tags: string[];
  statuses: string[];
  dateFrom: string;       // ISO YYYY-MM-DD or ''
  dateTo: string;         // ISO YYYY-MM-DD or ''
}

export interface SearchFiltersActions {
  setCategories: (values: string[]) => void;
  setType: (value: string) => void;
  setTags: (values: string[]) => void;
  setStatuses: (values: string[]) => void;
  setDateRange: (from: string, to: string) => void;
  clearFilter: (facet: keyof SearchFiltersState) => void;
  clearAll: () => void;
  hasActiveFilters: boolean;
  activeFilterCount: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Parse URL into filter state. */
export function parseFiltersFromURL(searchParams: URLSearchParams): SearchFiltersState {
  return {
    categories: searchParams.getAll('category'),
    type: searchParams.get('type') ?? '',
    tags: searchParams.getAll('tags'),
    statuses: searchParams.getAll('status'),
    dateFrom: searchParams.get('dateFrom') ?? '',
    dateTo: searchParams.get('dateTo') ?? '',
  };
}

/** Apply filter state to a URLSearchParams, preserving other params. */
export function applyFiltersToURL(
  base: URLSearchParams,
  filters: SearchFiltersState
): URLSearchParams {
  const next = new URLSearchParams(base);

  // Reset filter params
  next.delete('category');
  next.delete('type');
  next.delete('tags');
  next.delete('status');
  next.delete('dateFrom');
  next.delete('dateTo');
  // Reset to page 1 when filters change
  next.delete('page');

  for (const c of filters.categories) next.append('category', c);
  if (filters.type) next.set('type', filters.type);
  for (const t of filters.tags) next.append('tags', t);
  for (const s of filters.statuses) next.append('status', s);
  if (filters.dateFrom) next.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) next.set('dateTo', filters.dateTo);

  return next;
}

/**
 * Compute a normalized cache key from query + filters.
 * Keys sorted alphabetically; values within each key sorted alphabetically.
 *
 * @example
 *   computeCacheKey('react', { categories: ['kb', 'guides'], type: 'article', ... })
 *   // → 'q_react|categories_guides_kb|type_article'
 */
export function computeCacheKey(q: string, filters: SearchFiltersState): string {
  const parts: string[] = [`q_${q}`];

  if (filters.categories.length > 0) {
    parts.push(`category_${[...filters.categories].sort().join('_')}`);
  }
  if (filters.type) {
    parts.push(`type_${filters.type}`);
  }
  if (filters.tags.length > 0) {
    parts.push(`tags_${[...filters.tags].sort().join('_')}`);
  }
  if (filters.statuses.length > 0) {
    parts.push(`status_${[...filters.statuses].sort().join('_')}`);
  }
  if (filters.dateFrom) parts.push(`dateFrom_${filters.dateFrom}`);
  if (filters.dateTo) parts.push(`dateTo_${filters.dateTo}`);

  return parts.join('|');
}

/** Count active (non-empty) filters. */
export function countActiveFilters(filters: SearchFiltersState): number {
  return (
    filters.categories.length +
    (filters.type ? 1 : 0) +
    filters.tags.length +
    filters.statuses.length +
    (filters.dateFrom || filters.dateTo ? 1 : 0)
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

const DEBOUNCE_MS = 300;

/**
 * Manages faceted search filter state via URLSearchParams.
 *
 * @param onFiltersChange Called (debounced for UI changes) when filters change.
 */
export function useSearchFilters(
  onFiltersChange?: (filters: SearchFiltersState) => void
): SearchFiltersState & SearchFiltersActions {
  const [searchParams, setSearchParams] = useSearchParams();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onChangeRef = useRef(onFiltersChange);
  onChangeRef.current = onFiltersChange;

  // Parse current filters from URL
  const filters = useMemo(() => parseFiltersFromURL(searchParams), [searchParams]);

  // Notify on filter changes (debounced)
  const notifyChange = useCallback((newFilters: SearchFiltersState) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onChangeRef.current?.(newFilters);
    }, DEBOUNCE_MS);
  }, []);

  // Popstate: apply filters immediately (no debounce)
  useEffect(() => {
    const handler = () => {
      const params = new URLSearchParams(window.location.search);
      const newFilters = parseFiltersFromURL(params);
      // Cancel any pending debounced call
      if (debounceRef.current) clearTimeout(debounceRef.current);
      onChangeRef.current?.(newFilters);
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  // ── Setters (UI-triggered, debounced) ────────────────────────────────────

  const updateFilters = useCallback(
    (updater: (prev: SearchFiltersState) => SearchFiltersState) => {
      const newFilters = updater(parseFiltersFromURL(new URLSearchParams(window.location.search)));
      const next = applyFiltersToURL(new URLSearchParams(window.location.search), newFilters);
      setSearchParams(next, { replace: false });
      notifyChange(newFilters);
    },
    [setSearchParams, notifyChange]
  );

  const setCategories = useCallback(
    (values: string[]) => updateFilters((prev) => ({ ...prev, categories: values })),
    [updateFilters]
  );

  const setType = useCallback(
    (value: string) => updateFilters((prev) => ({ ...prev, type: value })),
    [updateFilters]
  );

  const setTags = useCallback(
    (values: string[]) => updateFilters((prev) => ({ ...prev, tags: values })),
    [updateFilters]
  );

  const setStatuses = useCallback(
    (values: string[]) => updateFilters((prev) => ({ ...prev, statuses: values })),
    [updateFilters]
  );

  const setDateRange = useCallback(
    (from: string, to: string) =>
      updateFilters((prev) => ({ ...prev, dateFrom: from, dateTo: to })),
    [updateFilters]
  );

  const clearFilter = useCallback(
    (facet: keyof SearchFiltersState) => {
      updateFilters((prev) => {
        const next = { ...prev };
        if (facet === 'categories') next.categories = [];
        else if (facet === 'type') next.type = '';
        else if (facet === 'tags') next.tags = [];
        else if (facet === 'statuses') next.statuses = [];
        else if (facet === 'dateFrom' || facet === 'dateTo') {
          next.dateFrom = '';
          next.dateTo = '';
        }
        return next;
      });
    },
    [updateFilters]
  );

  const clearAll = useCallback(() => {
    updateFilters(() => ({
      categories: [],
      type: '',
      tags: [],
      statuses: [],
      dateFrom: '',
      dateTo: '',
    }));
  }, [updateFilters]);

  const activeFilterCount = countActiveFilters(filters);
  const hasActiveFilters = activeFilterCount > 0;

  return {
    ...filters,
    setCategories,
    setType,
    setTags,
    setStatuses,
    setDateRange,
    clearFilter,
    clearAll,
    hasActiveFilters,
    activeFilterCount,
  };
}
